import { useEffect, useRef } from "react";
import { useT } from "../hooks/useT";

export default function PlatformSection() {
  const t = useT();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.05 }
    );
    ref.current?.querySelectorAll(".fade-in, .clip-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="platform" ref={ref} data-testid="platform-section" className="relative bg-[var(--bg-surface)] border-t border-b" style={{ borderColor: 'var(--rule)' }}>
      {/* Header area */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4 fade-in">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-px h-4" style={{ backgroundColor: 'var(--gold)', opacity: 0.3 }} />
              <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-[var(--text-tertiary)]">02</p>
            </div>
            <h2
              data-testid="platform-heading"
              className="clip-reveal font-serif text-3xl sm:text-4xl lg:text-5xl tracking-[0.06em] text-[var(--text-primary)]"
              style={{ fontWeight: 300 }}
            >
              {t.platform.heading}
            </h2>
          </div>
          <div className="lg:col-span-8 fade-in">
            <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75] mb-5 max-w-[600px]">{t.platform.p1}</p>
            <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75] max-w-[600px]">{t.platform.p2}</p>

            <div
              className="mt-10 pt-10 max-w-[600px] border-t fade-in"
              style={{ borderColor: "var(--rule)" }}
              data-testid="platform-intelligence"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-px h-4" style={{ backgroundColor: "var(--gold)", opacity: 0.3 }} />
                <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-[var(--text-tertiary)]">
                  {t.platform.intelligenceEyebrow}
                </p>
              </div>
              <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75] mb-4">
                {t.platform.intelligenceBody}
              </p>
              <p className="font-sans text-[13px] text-[var(--text-tertiary)] leading-relaxed">
                {t.platform.intelligenceBridge}
                <a
                  href="#vision"
                  className="text-[var(--text-secondary)] underline underline-offset-[5px] decoration-[rgba(158,138,90,0.45)] hover:decoration-[var(--gold)] transition-colors"
                >
                  {t.platform.intelligenceVisionLink}
                </a>
                {t.platform.intelligenceBridgeEnd}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 3 Vertical Pillars */}
      <div className="pillars-grid grid grid-cols-1 md:grid-cols-3 border-t fade-in" style={{ borderColor: 'var(--rule)' }}>
        {t.platform.pillars.map((pillar, i) => (
          <div
            key={i}
            data-testid={`platform-pillar-${i}`}
            className={`pillar-item flex flex-col justify-center px-8 md:px-12 py-20 md:py-32 md:min-h-[60vh] ${i < 2 ? 'border-b md:border-b-0 md:border-r' : ''}`}
            style={i < 2 ? { borderColor: 'rgba(158, 138, 90, 0.15)' } : undefined}
          >
            <p className="font-sans text-[11px] uppercase tracking-[0.2em] mb-6" style={{ color: 'var(--gold)', opacity: 0.5 }}>
              {String(i + 1).padStart(2, '0')}
            </p>
            <h3 className="font-serif text-xl md:text-2xl tracking-[0.06em] text-[var(--text-primary)] mb-6" style={{ fontWeight: 300 }}>
              {pillar.title}
            </h3>
            <p className="font-sans text-[15px] text-[var(--text-secondary)] leading-[1.75] max-w-[360px]">
              {pillar.text}
            </p>
          </div>
        ))}
      </div>

      {/* Portal note */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
        <p className="font-sans text-[13px] text-[var(--text-tertiary)] leading-relaxed">{t.platform.portalNote}</p>
      </div>
    </section>
  );
}
